import React from 'react';
import { AlertTriangle, RotateCw, Home } from 'lucide-react';
import { capture } from '../services/monitoring';

// ---------------------------------------------------------------------------
// What a seller sees when the app itself breaks.
//
// Without this, a render error unmounts the whole tree and leaves a WHITE
// PAGE. No message, no way forward, and nothing telling us it happened — the
// only signal is whether the seller bothers to report it. Half of them will
// assume the platform is down and go and do something else.
//
// So: say plainly that it broke, give the two things worth trying, and report
// it whether or not they say anything.
//
// The error text is NOT shown. A React stack means nothing to a seller and
// tells anyone else which file to look at; it goes to monitoring instead.
// ---------------------------------------------------------------------------

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Reported even when monitoring is off, where capture() is a no-op — the
    // console line is what a developer sees locally.
    console.error('[app] render failed:', error);
    capture(error, { componentStack: info?.componentStack?.slice(0, 2000) });
  }

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="min-h-screen bg-canvas text-ink flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <span className="inline-flex w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-600
                           items-center justify-center mb-4">
            <AlertTriangle size={22} />
          </span>
          <h1 className="text-xl font-bold">This page stopped working</h1>
          <p className="text-sm text-ink-2 mt-2 leading-relaxed">
            Something went wrong on our side, not yours. Nothing you had saved has been lost —
            reloading usually fixes it, and we have been told it happened.
          </p>

          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 text-white
                         text-sm font-semibold px-4 py-2.5 hover:bg-brand-600 transition"
            >
              <RotateCw size={15} /> Reload the page
            </button>
            {/* A hard navigation, not a router link: the router is part of what
                just failed, so asking it to move is asking the broken thing to
                fix itself. */}
            <a
              href="/dashboard"
              className="inline-flex items-center gap-1.5 rounded-lg border-[1px] border-line
                         text-sm font-semibold px-4 py-2.5 hover:bg-surface-3 transition"
            >
              <Home size={15} /> Go to dashboard
            </a>
          </div>

          <p className="text-xs text-ink-3 mt-6">
            If it keeps happening, tell us through Support and we will look at it.
          </p>
        </div>
      </div>
    );
  }
}
